import axios from 'axios';
import { secureStorage, getCSRFToken, isValidJWT } from '../utils/security';

const API = axios.create({
  baseURL:         process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
  timeout:         10000,
  withCredentials: true,         // send httpOnly cookies (Django session/CSRF)
  headers: { 'Content-Type': 'application/json' },
});

// ─── Request interceptor: attach JWT + CSRF ───
API.interceptors.request.use((config) => {
  const token = secureStorage.getToken();
  if (token && isValidJWT(token)) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // CSRF protection (Django requires this for POST/PUT/DELETE)
  const csrf = getCSRFToken();
  if (csrf) config.headers['X-CSRFToken'] = csrf;
  return config;
}, (err) => Promise.reject(err));

// ─── Response interceptor: handle 401/403 globally ───
API.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      secureStorage.clearToken();
      window.location.href = '/';
    }
    if (err.response?.status === 403) {
      console.warn('Erişim reddedildi.');
    }
    return Promise.reject(err);
  }
);

// ─── Auth ───
export const loginUser    = (data) => API.post('/auth/login/',    data);
export const registerUser = (data) => {
  // Supplier sign-up carries the vergi levhası → FormData; let the browser set
  // the multipart boundary. Uploads get more time than the default 10 s.
  const isForm = typeof FormData !== 'undefined' && data instanceof FormData;
  const role = isForm ? data.get('role') : data.role;
  const endpoint = role === 'supplier'
    ? '/auth/register/supplier/'
    : '/auth/register/customer/';
  return API.post(endpoint, data, isForm ? { headers: { 'Content-Type': undefined }, timeout: 60000 } : undefined);
};
// Supplier uploads / replaces their vergi levhası.
export const uploadTaxCertificate = (file) => {
  const fd = new FormData();
  fd.append('tax_certificate', file);
  return API.post('/auth/me/tax-certificate/', fd, { headers: { 'Content-Type': undefined }, timeout: 60000 });
};
export const logoutUser   = (data) => API.post('/auth/logout/', data);
export const getMeUser    = ()     => API.get('/auth/me/');
export const updateProfile = (data) => API.patch('/auth/me/', data);
// Self-service account deletion (anonymizes the account; re-confirms the password)
export const deleteAccount = (password) => API.post('/auth/delete-account/', { password });
export const verifyEmail  = (data) => API.post('/auth/verify-email/', data);
export const resendCode   = (data) => API.post('/auth/resend-code/', data);
export const requestPasswordReset = (data) => API.post('/auth/password-reset/request/', data);
export const confirmPasswordReset = (data) => API.post('/auth/password-reset/confirm/', data);

// ─── Admin: user management ───
export const adminCreateUser = (data)     => API.post('/auth/users/create/', data);
export const adminUpdateUser = (id, data) => API.patch(`/auth/users/${id}/update/`, data);
// Private file → fetched with the admin's token as a blob (it has no public URL).
export const adminGetTaxCertificate = (id) =>
  API.get(`/auth/users/${id}/tax-certificate/`, { responseType: 'blob', timeout: 60000 });
// Mark a supplier's vergi levhası as checked (public "doğrulandı" badge) or undo it.
export const adminVerifyTax = (id, verified) => API.patch(`/auth/users/${id}/verify-tax/`, { verified });

// ─── Public metrics ───
export const getPublicStats   = ()       => API.get('/parts/stats/');

// ─── Platform switches (registrations, maintenance) ───
export const getPlatform      = ()       => API.get('/auth/platform/');
export const updatePlatform   = (data)   => API.patch('/auth/platform/', data);

// ─── Public suppliers directory ───
export const getSuppliers     = (params) => API.get('/parts/suppliers/', { params });

// ─── Vehicle brand+model catalog (single source for all dropdowns) ───
export const getVehicles      = ()       => API.get('/parts/vehicles/');

// ─── Part catalog: Kategori → Alt Kategori → Parça (single source for part dropdowns) ───
export const getPartCatalog   = ()       => API.get('/parts/catalog/');

// ─── Admin analytics ───
export const getAdminAnalytics = ()      => API.get('/parts/admin/analytics/');

// ─── Reviews ───
export const createReview      = (data)  => API.post('/quotes/reviews/', data);
export const getSupplierReviews = (id)   => API.get(`/quotes/reviews/supplier/${id}/`);

// ─── VIN decode ───
export const decodeVin        = (vin)    => API.get(`/parts/vin/${vin}/`);

// ─── Part Requests ───
export const getRequests      = (params) => API.get('/parts/requests/list/', { params });
export const createRequest    = (data)   => {
  // When sending photos we pass FormData; drop the default JSON content-type so
  // the browser sets multipart/form-data with the correct boundary.
  const isForm = typeof FormData !== 'undefined' && data instanceof FormData;
  return API.post('/parts/requests/', data, isForm ? { headers: { 'Content-Type': undefined } } : undefined);
};
export const getRequestById   = (id)     => API.get(`/parts/requests/${id}/`);
export const updateRequest    = (id, d)  => API.patch(`/parts/requests/${id}/status/`, d);

// ─── Stock Parts ───
export const getStock         = (params) => API.get('/parts/stock/', { params });
export const getStockFacets   = (params) => API.get('/parts/stock/facets/', { params });
export const getMyStock       = ()       => API.get('/parts/stock/', { params: { mine: 1 } });
// FormData (with photos) → let the browser set the multipart boundary.
export const createStock      = (data)   => API.post('/parts/stock/add/', data,
  (typeof FormData !== 'undefined' && data instanceof FormData) ? { headers: { 'Content-Type': undefined }, timeout: 60000 } : undefined);
export const addStockImages   = (id, files) => {
  const fd = new FormData();
  files.forEach(f => fd.append('images', f));
  return API.post(`/parts/stock/${id}/images/`, fd, { headers: { 'Content-Type': undefined }, timeout: 60000 });
};
export const deleteStockImage = (imageId) => API.delete(`/parts/stock/images/${imageId}/`);
export const importStock      = (file, dryRun = false) => {
  const fd = new FormData();
  fd.append('file', file);
  if (dryRun) fd.append('dry_run', '1');
  return API.post('/parts/stock/import/', fd, { headers: { 'Content-Type': undefined }, timeout: 120000 });
};
export const STOCK_TEMPLATE_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/parts/stock/import/template/`;
export const updateStock      = (id, d)  => API.patch(`/parts/stock/${id}/`, d);
export const deleteStock      = (id)     => API.delete(`/parts/stock/${id}/delete/`);

// ─── Quotes ───
export const getQuotes        = (params) => API.get('/quotes/list/',          { params });
export const createQuote      = (data)   => {
  // FormData when photos are attached → let the browser set the multipart boundary.
  const isForm = typeof FormData !== 'undefined' && data instanceof FormData;
  return API.post('/quotes/', data, isForm ? { headers: { 'Content-Type': undefined } } : undefined);
};
export const getQuoteById     = (id)     => API.get(`/quotes/${id}/`);
export const acceptQuote      = (id)     => API.patch(`/quotes/${id}/accept/`);
export const rejectQuote      = (id)     => API.patch(`/quotes/${id}/reject/`);

// ─── Payments (iyzico) ───
export const startCheckout    = (quoteId) => API.post('/payments/checkout/', { quote: quoteId, contract_accepted: true });
export const getPayment       = (id)      => API.get(`/payments/${id}/`);
export const releasePayment   = (id)      => API.post(`/payments/${id}/release/`);
export const getSubMerchant   = ()        => API.get('/payments/submerchant/');
export const saveSubMerchant  = (data)    => API.post('/payments/submerchant/', data);
export const getOrders        = ()        => API.get('/payments/orders/');
export const requestRefund    = (id, data)=> API.post(`/payments/${id}/refund/`, data);
export const getRefunds       = ()        => API.get('/payments/refunds/');
export const processRefund    = (id, data)=> API.post(`/payments/refunds/${id}/process/`, data);
export const getPaymentAnalytics = ()     => API.get('/payments/admin/analytics/');

// ─── Chat ───
export const startConversation  = (data)   => API.post('/chat/conversations/',                    data);
export const getConversations   = ()       => API.get('/chat/conversations/list/');
export const getMessages        = (convId) => API.get(`/chat/conversations/${convId}/messages/`);
export const sendMessage        = (convId, data) => API.post(`/chat/conversations/${convId}/messages/send/`, data);
export const adminJoinChat      = (convId) => API.post(`/chat/conversations/${convId}/admin-join/`);
export const requestAdminHelp   = (data)   => API.post('/chat/help/',                             data);

export default API;
